import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OnGroupDataMessageArgs, WebPubSubClient } from '@azure/web-pubsub-client';
import { BehaviorSubject, Observable, ReplaySubject, Subject, from } from 'rxjs';
import { Batch, BatchOrder} from 'reg-hub-common';
import { EnvironmentUrlService } from '../../environment-url/environment-url.service';

@Injectable({
  providedIn: 'root'
})
export class BatchesHubService implements OnDestroy {
    private client: WebPubSubClient | undefined;
    private messagesSubject = new ReplaySubject<any>(1);  // stores last message, can be Subject if you prefer
    public messages$: Observable<any> = this.messagesSubject.asObservable();
  
    private batchState = 'BatchStateChanged';
    private batchOrderCreated = 'BatchOrderCreated';
    private batchOrderStaged = 'BatchOrderStaged';
    private batchOrderProcessed = 'BatchOrderProcessed';
    private batchOrderSubmitted = 'BatchOrderSubmitted';
    private batchOrderCompleted = 'BatchOrderCompleted';

    private joinedGroups = new Set<string>();
    private isConnected = false;

    // BehaviorSubjects to store batches
    private batchStateSource = new Subject<Batch>();
    batchState$ = this.batchStateSource.asObservable();
  
    private batchOrderCreatedSource = new Subject<BatchOrder>();
    batchOrderCreated$ = this.batchOrderCreatedSource.asObservable();

    private batchOrderStagedSource = new Subject<BatchOrder>();
    batchOrderStaged$ = this.batchOrderStagedSource.asObservable();

    private batchOrderProcessedSource = new Subject<BatchOrder>();
    batchOrderProcessed$ = this.batchOrderProcessedSource.asObservable();
  
    private batchOrderSubmittedSource = new Subject<BatchOrder>();
    batchOrderSubmitted$ = this.batchOrderSubmittedSource.asObservable();

    private batchOrderCompletedSource = new Subject<BatchOrder>();
    batchOrderCompleted$ = this.batchOrderCompletedSource.asObservable();
  
    constructor(private http: HttpClient,
      private environmentUrl: EnvironmentUrlService
    ) {}
  
    connect(): void {
      if (!this.isConnected) {
        this.http.get<any>(`${this.environmentUrl.batchUrlAddress}/Hubs/Batches/Negotiate`)
          .subscribe({
            next: response => this.completeConnection(response.url),
            error: err => console.error("Error negotiating URL:", err)
          });
      }
    }
  
    private completeConnection(url: string): void {
      this.client = new WebPubSubClient({
        getClientAccessUrl: async () => url
      });
  
      // Set up event handlers
      this.client.on("connected", (e) => {
        console.log("Connected to Batches hub:", e.connectionId);
        this.isConnected = true;
      });
  
      // handle messages to a group when received
      this.client.on("group-message", (messageEvent) => {
        this.handleGroupMessageReceived(messageEvent);
      });
  
      // handle disconnection from the hub
      this.client.on("disconnected", () => {
        console.warn("Disconnected from Web PubSub");
        this.isConnected = false;
      });
  
      // Start the connection
      from(this.client.start()).subscribe({
        next: () => {
          console.log('Client started');
        },
        error: err => console.error('Error starting client', err)
      });
    }
  
    private joinGroupIfNotJoined(groupName: string) {
      if (!this.joinedGroups.has(groupName)) {
        this.client?.joinGroup(groupName)
          .then(() => {
            this.joinedGroups.add(groupName);
          })
      }
    }
  
    private leaveGroupIfJoined(groupName: string) {
      if (this.joinedGroups.has(groupName)) {
        this.client?.leaveGroup(groupName)
          .then(() => {
            this.joinedGroups.delete(groupName);
          })
      }
    }

    joinBatchStateGrouo(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchState}-${batchID}`);
      console.log('Join Batch State Hub');
    }

    joinBatchOrderCreatedGrouo(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchOrderCreated}-${batchID}`);
      console.log('Join Batch Order Created Hub');
    }

    joinBatchOrderStagedGrouo(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchOrderStaged}-${batchID}`);
      console.log('Join Batch Order Staged Hub');
    }

    joinBatchOrderProcessedGrouo(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchOrderProcessed}-${batchID}`);
      console.log('Join Batch Order Processed Hub');
    }

    joinBatchOrderSubmittedGrouo(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchOrderSubmitted}-${batchID}`);
      console.log('Join Batch Order Submitted Hub');
    }

    joinBatchOrderCompletedGrouo(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchOrderCompleted}-${batchID}`);
      console.log('Join Batch Order Completed Hub');
    }
  
    joinAllBatchGroups(batchID: string) {
      this.joinGroupIfNotJoined(`${this.batchState}-${batchID}`);
      this.joinGroupIfNotJoined(`${this.batchOrderCreated}-${batchID}`);
      this.joinGroupIfNotJoined(`${this.batchOrderStaged}-${batchID}`);
      this.joinGroupIfNotJoined(`${this.batchOrderProcessed}-${batchID}`);
      this.joinGroupIfNotJoined(`${this.batchOrderSubmitted}-${batchID}`);
      this.joinGroupIfNotJoined(`${this.batchOrderCompleted}-${batchID}`);
    }
  
    leaveBatchStateGroup(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchState}-${batchID}`);
      console.log('Leave Batch State Hub');
    }

    leaveBatchOrderCreatedGroup(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchOrderCreated}-${batchID}`);
      console.log('Leave Batch Order Created Hub');
    }

    leaveBatchOrderStagedGroup(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchOrderStaged}-${batchID}`);
      console.log('Leave Batch Order Staged Hub');
    }

    leaveBatchOrderProcessedGroup(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchOrderProcessed}-${batchID}`);
      console.log('Leave Batch Order Processed Hub');
    }

    leaveBatchOrderSubmittedGroup(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchOrderSubmitted}-${batchID}`);
      console.log('Leave Batch Order Submitted Hub');
    }

    leaveBatchOrderCompletedGroup(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchOrderCompleted}-${batchID}`);
      console.log('Leave Batch Order Completed Hub');
    }

    leaveAllBatchGroupsGroups(batchID: string) {
      this.leaveGroupIfJoined(`${this.batchState}-${batchID}`);
      this.leaveGroupIfJoined(`${this.batchOrderCreated}-${batchID}`);
      this.leaveGroupIfJoined(`${this.batchOrderStaged}-${batchID}`);
      this.leaveGroupIfJoined(`${this.batchOrderProcessed}-${batchID}`);
      this.leaveGroupIfJoined(`${this.batchOrderSubmitted}-${batchID}`);
      this.leaveGroupIfJoined(`${this.batchOrderCompleted}-${batchID}`);
    }
  
    handleGroupMessageReceived(messageEvent: OnGroupDataMessageArgs) {
      console.log("Received Batch message");
      if (messageEvent.message.dataType === "text") {
        const group: string = messageEvent.message.group;
        const rawMessage = JSON.parse(messageEvent.message.data as string, this.toCamelCase);
        console.log("Received Batch message:", rawMessage);
  
        switch (group.split("-")[0]) {
          case this.batchState:
            const batch = rawMessage as Batch;
            this.batchStateSource.next(batch);
            break;
          case this.batchOrderCreated:
            const createdBatchOrder = rawMessage as BatchOrder;
            this.batchOrderCreatedSource.next(createdBatchOrder);
            break;
          case this.batchOrderStaged:
            const stagedBatchOrder = rawMessage as BatchOrder;
            this.batchOrderStagedSource.next(stagedBatchOrder);
            break;
          case this.batchOrderProcessed:
            const processedBatchOrder = rawMessage as BatchOrder;
            this.batchOrderProcessedSource.next(processedBatchOrder);
            break;
          case this.batchOrderSubmitted:
            const submittedBatchOrder = rawMessage as BatchOrder;
            this.batchOrderSubmittedSource.next(submittedBatchOrder);
            break;
          case this.batchOrderCompleted:
            const completedBatchOrder = rawMessage as BatchOrder;
            this.batchOrderCompletedSource.next(completedBatchOrder);
            break;
          default:
            // This shouldnt happen, we only subscribed to the three groups here? Maybe for Orders?
            console.warn(`Received a message from an unhandled group: ${group}`, rawMessage);
            break;
        }
  
        this.messagesSubject.next(rawMessage);
      }
    } 
  
    ngOnDestroy() {
      // Stop the connection if the service is destroyed (e.g., in certain testing scenarios)
      if (this.client) {
        this.client.stop();
      }
    }
  
    toCamelCase(key: any, value: any) {
      if (value && typeof value === 'object'){
        for (var k in value) {
          // Special case: If the key is exactly 'ID', rename it to 'id'
          if (k === 'ID') {
            value['id'] = value[k];
            delete value[k];
          }
          // Otherwise, just lowercase the first letter
          else if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
            value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
            delete value[k];
          }
        }
      }
      return value;
    }
}